import { Injectable, inject } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signOut, authState } from '@angular/fire/auth';

const SESSION_MAX_MINUTES = 60; // 1 hour

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private auth = inject(Auth);

  userData: any;

  constructor() {
    authState(this.auth).subscribe((user) => {
      if (user) {
        this.userData = user;
        localStorage.setItem('user', JSON.stringify(this.userData));
      } else {
        localStorage.setItem('user', 'null');
      }
    });

    // Check session timeout when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkSessionTimeout().catch(() => undefined);
      }
    });
  }

  isUserLoggedIn(): boolean {
    const user = JSON.parse(localStorage.getItem('user')!);
    return user !== null;
  }

  isSessionExpired(): boolean {
    const loginTime = localStorage.getItem('medpal_login_time');
    if (!loginTime) return false;
    const loginTimeMs = parseInt(loginTime, 10);
    if (isNaN(loginTimeMs)) return false;
    const minutesDiff = (Date.now() - loginTimeMs) / (1000 * 60);
    return minutesDiff >= SESSION_MAX_MINUTES;
  }

  async checkSessionTimeout(): Promise<boolean> {
    if (!this.isSessionExpired()) return false;
    try {
      await this.signOut();
    } catch {
      /* ignore sign-out failures so local cleanup still happens */
    }
    sessionStorage.setItem('medpal_show_session_timeout', '1');
    window.location.replace('/user/login');
    return true;
  }

  async login(email: string, password: string): Promise<void> {
    try {
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      if (result.user) {
        const idTokenResult = await result.user.getIdTokenResult(true);
        const roles = idTokenResult.claims['roles'];
        const isAdmin = Array.isArray(roles) && roles.includes('admin');
        if (!isAdmin) {
          throw new Error('User is not an admin');
        }
        localStorage.setItem('medpal_login_time', new Date().getTime().toString());
      } else {
        throw new Error('User not found');
      }
    } catch (error) {
      throw error;
    }
  }

  async signOut(): Promise<void> {
    await signOut(this.auth);
    localStorage.removeItem('user');
    localStorage.removeItem('medpal_login_time');
  }

  /**
   * Canonical "session expired, send the user to login" path. Used by the
   * server-driven SessionTimeoutService and the HTTP 401/403 interceptor so
   * they cannot drift from each other (or from the local checkSessionTimeout
   * path) as we add storage keys over time.
   */
  async forceTimeoutSignOut(returnUrl?: string | null): Promise<void> {
    try {
      await this.signOut();
    } catch {
      /* keep going so local state still gets cleaned up */
    }
    try {
      sessionStorage.setItem('medpal_show_session_timeout', '1');
      if (returnUrl && returnUrl !== '/user/login') {
        sessionStorage.setItem('medpal_return_url', returnUrl);
      }
    } catch {
      /* storage unavailable in some browser modes; redirect anyway */
    }
    window.location.replace('/user/login');
  }
}
